import { PDFDocument, StandardFonts } from 'pdf-lib';
import { searchCarriers } from '../../api/figApi';

const BLANK_PDF_URL = '/acord/ACORD_130_blank.pdf';

/**
 * Generate a filled ACORD 130 — Workers Compensation Application.
 */
export default async function generateACORD130(formData, insured, getToken) {
  const res = await fetch(BLANK_PDF_URL);
  if (!res.ok) throw new Error(`Failed to load blank ACORD 130 (${res.status})`);
  const pdfBytes = await res.arrayBuffer();

  const pdfDoc = await PDFDocument.load(pdfBytes);
  const form = pdfDoc.getForm();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  // Dump all field names on first run for debugging mismatches
  const fields = form.getFields();
  console.log(`[ACORD 130] Total form fields: ${fields.length}`);
  console.log('[ACORD 130] All field names:');
  fields.forEach(field => {
    console.log(`  ${field.constructor.name}: "${field.getName()}"`);
  });

  function setText(name, value) {
    if (!value && value !== 0) return;
    try {
      const field = form.getTextField(name);
      field.setText(String(value));
      try { field.updateAppearances(font); } catch {}
      console.log(`[ACORD 130] Filled "${name}" = "${value}"`);
    } catch (err) {
      console.warn(`[ACORD 130] Text field NOT FOUND: "${name}"`);
    }
  }

  function setCheck(name) {
    try {
      form.getCheckBox(name).check();
      console.log(`[ACORD 130] Checked "${name}"`);
    } catch (err) {
      console.warn(`[ACORD 130] Checkbox NOT FOUND: "${name}"`);
    }
  }

  const f = formData;
  const ins = insured || {};
  const fmtNum = (v) => v ? Number(v).toLocaleString() : '';
  const today = new Date().toISOString().split('T')[0];

  console.log('[generateACORD130] EL limits from formData:', {
    elEachAccident: f.elEachAccident,
    elDiseasePolicy: f.elDiseasePolicy,
    elDiseaseEachEmployee: f.elDiseaseEachEmployee,
    totalEstimatedPremium: f.totalEstimatedPremium,
    entityType: f.entityType,
    partOneStates: f.partOneStates,
  });
  console.log('[generateACORD130] Questions from formData:', f.questions);
  console.log('[generateACORD130] Question explanations:', f.questionExplanations);

  // ============ PAGE 1 — HEADER ============
  try { setText('Form_CompletionDate_A', f.date || today); } catch {}

  // Producer (hardcoded FIG)
  try { setText('Producer_FullName_A', 'Fragos Insurance Group LLC'); } catch {}
  try { setText('Producer_MailingAddress_LineOne_A', '18203 FM 1488'); } catch {}
  try { setText('Producer_MailingAddress_LineTwo_A', ''); } catch {}
  try { setText('Producer_MailingAddress_CityName_A', 'Magnolia'); } catch {}
  try { setText('Producer_MailingAddress_StateOrProvinceCode_A', 'TX'); } catch {}
  try { setText('Producer_MailingAddress_PostalCode_A', '77354'); } catch {}
  try { setText('Producer_ContactPerson_FullName_A', 'Marc Fragos'); } catch {}
  try { setText('Producer_ContactPerson_PhoneNumber_A', ''); } catch {}
  try { setText('Producer_ContactPerson_CellPhoneNumber_A', ''); } catch {}
  try { setText('Producer_ContactPerson_EmailAddress_A', 'marc@myfiginsurance.com'); } catch {}

  // Carrier
  try { setText('Insurer_FullName_A', f.carrier); } catch {}
  try { setText('Insurer_ProducerIdentifier_A', ''); } catch {}
  try { setText('Insurer_SubProducerIdentifier_A', ''); } catch {}
  try { setText('Insurer_Underwriter_FullName_A', ''); } catch {}

  // Carrier address lookup
  if (getToken && f.naicCode) {
    try {
      const token = await getToken();
      const results = await searchCarriers(token, f.carrier || '');
      const match = results.find(c => String(c.NAICCode) === String(f.naicCode));
      if (match) {
        try { setText('Insurer_FullName_A', match.CompanyName || f.carrier); } catch {}
      }
    } catch {}
  }

  // ============ NAMED INSURED ============
  try { setText('NamedInsured_FullName_A', ins.CompanyName || ''); } catch {}
  try { setText('NamedInsured_Primary_PhoneNumber_A', ins.BusinessPhone || ins.Phone || ''); } catch {}
  try { setText('NamedInsured_Secondary_PhoneNumber_A', ins.Phone2 || ''); } catch {}
  try { setText('NamedInsured_MailingAddress_LineOne_A', ins.MailingAddress || ins.Address || ''); } catch {}
  try { setText('NamedInsured_MailingAddress_LineTwo_A', ins.AddressLine2 || ''); } catch {}
  try { setText('NamedInsured_MailingAddress_CityName_A', ins.MailingCity || ins.City || ''); } catch {}
  try { setText('NamedInsured_MailingAddress_StateOrProvinceCode_A', ins.MailingState || ins.State || ''); } catch {}
  try { setText('NamedInsured_MailingAddress_PostalCode_A', ins.MailingZip || ins.Zip || ''); } catch {}
  try { setText('NamedInsured_InBusinessYearCount_A', ins.YearsInBusiness || ''); } catch {}
  try { setText('NamedInsured_SICCode_A', ins.SICCode || ''); } catch {}
  try { setText('NamedInsured_NAICSCode_A', ins.NAICSCode || ''); } catch {}
  try { setText('NamedInsured_Primary_WebsiteAddress_A', ins.WebsiteAddress || ins.Website || ''); } catch {}
  try { setText('NamedInsured_Primary_EmailAddress_A', ins.EmailAddress || ins.Email || ''); } catch {}
  try { setText('NamedInsured_TaxIdentifier_A', ins.FEIN || ins.TaxIdentifier || ''); } catch {}
  try { setText('NamedInsured_NCCIRiskIdentifier_A', ins.NCCIRiskId || ''); } catch {}
  try { setText('NamedInsured_RatingBureauIdentifier_A', ins.RatingBureauId || ''); } catch {}
  try { setText('NamedInsured_CreditBureauName_A', ''); } catch {}
  try { setText('NamedInsured_CreditBureauIdentifier_A', ''); } catch {}

  // Entity type checkboxes
  const entityMap = {
    soleProprietor: 'NamedInsured_LegalEntity_SoleProprietorIndicator_A',
    partnership: 'NamedInsured_LegalEntity_PartnershipIndicator_A',
    corporation: 'NamedInsured_LegalEntity_CorporationIndicator_A',
    subchapterS: 'NamedInsured_LegalEntity_SubchapterSCorporationIndicator_A',
    llc: 'NamedInsured_LegalEntity_LimitedLiabilityCorporationIndicator_A',
    jointVenture: 'NamedInsured_LegalEntity_JointVentureIndicator_A',
    trust: 'NamedInsured_LegalEntity_TrustIndicator_A',
    unincorporatedAssoc: 'NamedInsured_LegalEntity_UnincorporatedAssociationIndicator_A',
    other: 'NamedInsured_LegalEntity_OtherIndicator_A',
  };
  if (f.entityType && entityMap[f.entityType]) {
    try { setCheck(entityMap[f.entityType]); } catch {}
  }
  try { setText('NamedInsured_LegalEntity_OtherDescription_A', f.entityOtherDesc || ''); } catch {}

  // ============ POLICY STATUS CHECKBOXES ============
  if (f.policyStatus === 'quote') try { setCheck('Policy_Status_QuoteIndicator_A'); } catch {}
  if (f.policyStatus === 'bound') try { setCheck('Policy_Status_BoundIndicator_A'); } catch {}
  if (f.policyStatus === 'assignedRisk') try { setCheck('Policy_Status_AssignedRiskIndicator_A'); } catch {}
  if (f.policyStatus === 'issue') try { setCheck('Policy_Status_IssueIndicator_A'); } catch {}

  // ============ PAYMENT CHECKBOXES ============
  if (f.paymentType === 'producerBill') try { setCheck('Policy_Payment_ProducerBillIndicator_A'); } catch {}
  if (f.paymentType === 'directBill') try { setCheck('Policy_Payment_DirectBillIndicator_A'); } catch {}
  if (f.paymentFreq === 'annual') try { setCheck('Policy_Payment_AnnualIndicator_A'); } catch {}
  if (f.paymentFreq === 'semiAnnual') try { setCheck('Policy_Payment_SemiAnnualIndicator_A'); } catch {}
  if (f.paymentFreq === 'quarterly') try { setCheck('Policy_Payment_QuarterlyIndicator_A'); } catch {}
  if (f.paymentFreq === 'other') try { setCheck('Policy_Payment_OtherIndicator_A'); } catch {}
  try { setText('Policy_Payment_PaymentScheduleCode_A', f.paymentSchedule || ''); } catch {}
  try { setText('Policy_Payment_DownPaymentPercent_A', f.downPaymentPct || ''); } catch {}

  // ============ AUDIT CHECKBOXES ============
  if (f.auditFreq === 'atExpiration') try { setCheck('Policy_Audit_AtExpirationIndicator_A'); } catch {}
  if (f.auditFreq === 'semiAnnual') try { setCheck('Policy_Audit_SemiAnnualIndicator_A'); } catch {}
  if (f.auditFreq === 'quarterly') try { setCheck('Policy_Audit_QuarterlyIndicator_A'); } catch {}
  if (f.auditFreq === 'monthly') try { setCheck('Policy_Audit_MonthlyIndicator_A'); } catch {}
  if (f.auditFreq === 'other') try { setCheck('Policy_Audit_OtherIndicator_A'); } catch {}
  try { setText('Policy_Audit_FrequencyCode_A', f.auditFreqCode || ''); } catch {}

  // ============ PARTICIPATING ============
  if (f.participating === 'yes') try { setCheck('Policy_ParticipatingIndicator_A'); } catch {}
  if (f.participating === 'no') try { setCheck('Policy_NonParticipatingIndicator_A'); } catch {}

  // ============ BINDER / POLICY DATES ============
  try { setText('Binder_EffectiveDate_A', f.effectiveDate || ''); } catch {}
  try { setText('Policy_EffectiveDate_A', f.effectiveDate || ''); } catch {}
  try { setText('Policy_ExpirationDate_A', f.expirationDate || ''); } catch {}
  try { setText('Policy_RatingEffectiveDate_A', f.ratingEffDate || ''); } catch {}
  try { setText('Policy_NormalAnniversaryRatingDate_A', f.anniversaryDate || ''); } catch {}
  try { setText('Policy_RetrospectiveRatingPlan_A', f.retrospectiveRatingPlan || ''); } catch {}

  // ============ 3 LOCATIONS (A, B, C) ============
  const locSuffixes = ['_A', '_B', '_C'];
  (f.locations || []).forEach((loc, i) => {
    if (i > 2) return;
    const s = locSuffixes[i];
    try { setText(`Location_ProducerIdentifier${s}`, loc.locNum); } catch {}
    try { setText(`Location_HighestFloorCount${s}`, loc.highestFloor); } catch {}
    try { setText(`Location_PhysicalAddress_LineOne${s}`, loc.address); } catch {}
    try { setText(`Location_PhysicalAddress_LineTwo${s}`, loc.addressLine2); } catch {}
    try { setText(`Location_PhysicalAddress_CityName${s}`, loc.city); } catch {}
    try { setText(`Location_PhysicalAddress_CountyName${s}`, loc.county); } catch {}
    try { setText(`Location_PhysicalAddress_StateOrProvinceCode${s}`, loc.state); } catch {}
    try { setText(`Location_PhysicalAddress_PostalCode${s}`, loc.zip); } catch {}
  });

  // ============ PART ONE — WC STATES (10 slots A-J) ============
  const stateSuffixes10 = ['_A','_B','_C','_D','_E','_F','_G','_H','_I','_J'];
  (f.partOneStates || []).forEach((st, i) => {
    if (i > 9) return;
    try { setText(`WorkersCompensation_PartOne_StateOrProvinceCode${stateSuffixes10[i]}`, st); } catch {}
  });

  // ============ EL LIMITS ============
  try { setText('WorkersCompensationEmployersLiability_EmployersLiability_EachAccidentLimitAmount_A', fmtNum(f.elEachAccident)); } catch {}
  try { setText('WorkersCompensationEmployersLiability_EmployersLiability_DiseasePolicyLimitAmount_A', fmtNum(f.elDiseasePolicy)); } catch {}
  try { setText('WorkersCompensationEmployersLiability_EmployersLiability_DiseaseEachEmployeeLimitAmount_A', fmtNum(f.elDiseaseEachEmployee)); } catch {}

  // ============ PART THREE — OTHER STATES (10 slots A-J) ============
  (f.partThreeStates || []).forEach((st, i) => {
    if (i > 9) return;
    try { setText(`WorkersCompensation_PartThree_StateOrProvinceCode${stateSuffixes10[i]}`, st); } catch {}
  });

  // ============ DEDUCTIBLE ============
  if (f.deductibleType === 'medical') try { setCheck('WorkersCompensation_DeductibleType_MedicalIndicator_A'); } catch {}
  if (f.deductibleType === 'indemnity') try { setCheck('WorkersCompensation_DeductibleType_IndemnityIndicator_A'); } catch {}
  if (f.deductibleType === 'other') try { setCheck('WorkersCompensation_DeductibleType_OtherIndicator_A'); } catch {}
  try { setText('WorkersCompensation_DeductibleType_OtherDescription_A', f.deductibleTypeOther || ''); } catch {}
  try { setText('WorkersCompensation_DeductibleAmount_A', fmtNum(f.deductibleAmount)); } catch {}

  // ============ COVERAGE OPTIONS ============
  if (f.covUSLH) try { setCheck('WorkersCompensation_Coverage_USLHIndicator_A'); } catch {}
  if (f.covVoluntary) try { setCheck('WorkersCompensation_Coverage_VoluntaryCompensationIndicator_A'); } catch {}
  if (f.covForeign) try { setCheck('WorkersCompensation_Coverage_ForeignCoverageIndicator_A'); } catch {}
  if (f.covManagedCare) try { setCheck('WorkersCompensation_Coverage_ManagedCareOptionIndicator_A'); } catch {}
  if (f.covOtherA) try { setCheck('WorkersCompensation_Coverage_OtherIndicator_A'); } catch {}
  if (f.covOtherB) try { setCheck('WorkersCompensation_Coverage_OtherIndicator_B'); } catch {}
  try { setText('WorkersCompensation_Coverage_OtherDescription_A', f.covOtherDescA || ''); } catch {}
  try { setText('WorkersCompensation_Coverage_OtherDescription_B', f.covOtherDescB || ''); } catch {}
  try { setText('WorkersCompensation_DividendOrSafetyPlan_A', f.dividendPlan || ''); } catch {}
  try { setText('WorkersCompensation_AdditionalCompanyInformation_A', f.additionalCompanyInfo || ''); } catch {}
  try { setText('WorkersCompensation_AdditionalCoverageEndorsementDescription_A', f.additionalEndorsements || ''); } catch {}

  // ============ PREMIUM TOTALS ============
  try { setText('WorkersCompensationLineOfBusiness_TotalEstimatedAnnualPremiumAllStatesAmount_A', fmtNum(f.totalEstimatedPremium)); } catch {}
  try { setText('WorkersCompensationLineOfBusiness_TotalMinimumPremiumAllStatesAmount_A', fmtNum(f.totalMinimumPremium)); } catch {}
  try { setText('WorkersCompensationLineOfBusiness_TotalDepositPremiumAllStatesAmount_A', fmtNum(f.totalDepositPremium)); } catch {}

  // ============ CONTACTS ============
  try { setText('NamedInsured_InspectionContact_FullName_A', f.inspectionName || ''); } catch {}
  try { setText('NamedInsured_InspectionContact_PhoneNumber_A', f.inspectionPhone || ''); } catch {}
  try { setText('NamedInsured_InspectionContact_CellPhoneNumber_A', f.inspectionCell || ''); } catch {}
  try { setText('NamedInsured_InspectionContact_EmailAddress_A', f.inspectionEmail || ''); } catch {}
  try { setText('NamedInsured_AccountingContact_FullName_A', f.accountingName || ''); } catch {}
  try { setText('NamedInsured_AccountingContact_PhoneNumber_A', f.accountingPhone || ''); } catch {}
  try { setText('NamedInsured_AccountingContact_CellPhoneNumber_A', f.accountingCell || ''); } catch {}
  try { setText('NamedInsured_AccountingContact_EmailAddress_A', f.accountingEmail || ''); } catch {}
  try { setText('NamedInsured_ClaimContact_FullName_A', f.claimsName || ''); } catch {}
  try { setText('NamedInsured_ClaimContact_PhoneNumber_A', f.claimsPhone || ''); } catch {}
  try { setText('NamedInsured_ClaimContact_CellPhoneNumber_A', f.claimsCell || ''); } catch {}
  try { setText('NamedInsured_ClaimContact_EmailAddress_A', f.claimsEmail || ''); } catch {}

  // ============ INDIVIDUAL / OFFICER SCHEDULE (4 rows A-D) ============
  const indSuffixes = ['_A', '_B', '_C', '_D'];
  (f.individuals || []).forEach((ind, i) => {
    if (i > 3) return;
    const s = indSuffixes[i];
    try { setText(`WorkersCompensation_Individual_StateOrProvinceCode${s}`, ind.state); } catch {}
    try { setText(`WorkersCompensation_Individual_LocationProducerIdentifier${s}`, ind.location); } catch {}
    try { setText(`WorkersCompensation_Individual_FullName${s}`, ind.name); } catch {}
    try { setText(`WorkersCompensation_Individual_BirthDate${s}`, ind.dob); } catch {}
    try { setText(`WorkersCompensation_Individual_TitleRelationshipCode${s}`, ind.title); } catch {}
    try { setText(`WorkersCompensation_Individual_OwnershipPercent${s}`, ind.ownership); } catch {}
    try { setText(`WorkersCompensation_Individual_DutiesDescription${s}`, ind.duties); } catch {}
    try { setText(`WorkersCompensation_Individual_IncludedExcludedCode${s}`, ind.includedExcluded); } catch {}
    try { setText(`WorkersCompensation_Individual_RatingClassificationCode${s}`, ind.classCode); } catch {}
    try { setText(`WorkersCompensation_Individual_RemunerationAmount${s}`, fmtNum(ind.remuneration)); } catch {}
  });

  // ============ PAGE 2 — CLASSIFICATION SCHEDULE (14 rows A-N) ============
  try { setText('Producer_CustomerIdentifier_A', ''); } catch {}
  try { setText('WorkersCompensation_RateState_PageNumber_A', ''); } catch {}
  try { setText('WorkersCompensation_RateState_TotalPageNumber_A', ''); } catch {}
  try { setText('WorkersCompensation_RateState_StateOrProvinceName_A_A', f.stateName || ''); } catch {}

  const classSuffixes = ['_A','_B','_C','_D','_E','_F','_G','_H','_I','_J','_K','_L','_M','_N'];
  (f.classifications || []).forEach((cls, i) => {
    if (i > 13) return;
    const s = classSuffixes[i];
    try { setText(`WorkersCompensation_RateClass_LocationProducerIdentifier${s}`, cls.location); } catch {}
    try { setText(`WorkersCompensation_RateClass_ClassificationCode${s}`, cls.classCode); } catch {}
    try { setText(`WorkersCompensation_RateClass_DescriptionCode${s}`, cls.description); } catch {}
    try { setText(`WorkersCompensation_RateClass_DutiesDescription${s}`, cls.duties); } catch {}
    try { setText(`WorkersCompensation_RateClass_FullTimeEmployeeCount${s}`, cls.ftEmployees); } catch {}
    try { setText(`WorkersCompensation_RateClass_PartTimeEmployeeCount${s}`, cls.ptEmployees); } catch {}
    try { setText(`WorkersCompensation_RateClass_SICCode${s}`, cls.sic); } catch {}
    try { setText(`WorkersCompensation_RateClass_NAICSCode${s}`, cls.naics); } catch {}
    try { setText(`WorkersCompensation_RateClass_RemunerationAmount${s}`, fmtNum(cls.remuneration)); } catch {}
    try { setText(`WorkersCompensation_RateClass_Rate${s}`, cls.rate); } catch {}
    try { setText(`WorkersCompensation_RateClass_EstimatedManualPremiumAmount${s}`, fmtNum(cls.manualPremium)); } catch {}
  });

  // ============ STATE PREMIUM CALCULATIONS ============
  try { setText('WorkersCompensation_RateState_StateOrProvinceName_A_B', f.stateName || ''); } catch {}
  const sm = f.stateModifiers || {};
  try { setText('WorkersCompensationStateCoverage_TotalFactoredPremiumAmount_A', fmtNum(f.stateTotalFactored)); } catch {}
  try { setText('WorkersCompensationStateCoverage_IncreasedLimits_ModificationFactor_A', sm.increasedLimitsMod); } catch {}
  try { setText('WorkersCompensationStateCoverage_IncreasedLimits_FactoredPremiumAmount_A', fmtNum(sm.increasedLimitsPrem)); } catch {}
  try { setText('WorkersCompensationStateCoverage_Deductible_ModificationFactor_A', sm.deductibleMod); } catch {}
  try { setText('WorkersCompensationStateCoverage_Deductible_FactoredPremiumAmount_A', fmtNum(sm.deductiblePrem)); } catch {}
  try { setText('WorkersCompensationStateCoverage_ExperienceOrMerit_ModificationFactor_A', sm.experienceMod); } catch {}
  try { setText('WorkersCompensationStateCoverage_ExperienceOrMerit_FactoredPremiumAmount_A', fmtNum(sm.experiencePrem)); } catch {}
  try { setText('WorkersCompensationStateCoverage_Terrorism_PremiumAmount_A', fmtNum(sm.terrorismPrem)); } catch {}
  try { setText('WorkersCompensationStateCoverage_Catastrophe_PremiumAmount_A', fmtNum(sm.catastrophePrem)); } catch {}
  try { setText('WorkersCompensationStateCoverage_AssignedRiskSurcharge_ModificationFactor_A', sm.assignedRiskSurchargeMod); } catch {}
  try { setText('WorkersCompensationStateCoverage_AssignedRiskSurcharge_FactoredPremiumAmount_A', fmtNum(sm.assignedRiskSurchargePrem)); } catch {}
  try { setText('WorkersCompensationStateCoverage_AssignedRiskAdditionalPremium_ModificationFactor_A', sm.assignedRiskAddlMod); } catch {}
  try { setText('WorkersCompensationStateCoverage_AssignedRiskAdditionalPremium_FactoredPremiumAmount_A', fmtNum(sm.assignedRiskAddlPrem)); } catch {}
  try { setText('WorkersCompensationStateCoverage_Other_CoverageDescription_A', sm.otherDescA); } catch {}
  try { setText('WorkersCompensationStateCoverage_Other_ModificationFactor_A', sm.otherModA); } catch {}
  try { setText('WorkersCompensationStateCoverage_Other_FactoredPremiumAmount_A', fmtNum(sm.otherPremA)); } catch {}
  try { setText('WorkersCompensationStateCoverage_ScheduleRating_ModificationFactor_A', sm.scheduleRatingMod); } catch {}
  try { setText('WorkersCompensationStateCoverage_ScheduleRating_FactoredPremiumAmount_A', fmtNum(sm.scheduleRatingPrem)); } catch {}
  try { setText('WorkersCompensationStateCoverage_ContractingClassPremiumAdjustmentProgram_ModificationFactor_A', sm.contractingClassMod); } catch {}
  try { setText('WorkersCompensationStateCoverage_ContractingClassPremiumAdjustmentProgram_FactoredPremiumAmount_A', fmtNum(sm.contractingClassPrem)); } catch {}
  try { setText('WorkersCompensationStateCoverage_StandardPremium_ModificationFactor_A', sm.standardPremMod); } catch {}
  try { setText('WorkersCompensationStateCoverage_StandardPremium_FactoredPremiumAmount_A', fmtNum(sm.standardPremPrem)); } catch {}
  try { setText('WorkersCompensationStateCoverage_PremiumDiscount_ModificationFactor_A', sm.premDiscountMod); } catch {}
  try { setText('WorkersCompensationStateCoverage_PremiumDiscount_FactoredPremiumAmount_A', fmtNum(sm.premDiscountPrem)); } catch {}
  try { setText('WorkersCompensationStateCoverage_ExpenseConstant_PremiumAmount_A', fmtNum(sm.expenseConstantPrem)); } catch {}
  try { setText('WorkersCompensationStateCoverage_TaxesFeeAssessment_PremiumAmount_A', fmtNum(sm.taxesFeesPrem)); } catch {}
  try { setText('WorkersCompensationStateCoverage_Other_CoverageDescription_B', sm.otherDescB); } catch {}
  try { setText('WorkersCompensationStateCoverage_Other_ModificationFactor_B', sm.otherModB); } catch {}
  try { setText('WorkersCompensationStateCoverage_Other_FactoredPremiumAmount_B', fmtNum(sm.otherPremB)); } catch {}

  // State totals
  try { setText('WorkersCompensation_RateState_TotalEstimatedAnnualPremiumAmount_A', fmtNum(f.stateEstimatedPrem)); } catch {}
  try { setText('WorkersCompensation_RateState_MinimumPremiumAmount_A', fmtNum(f.stateMinPrem)); } catch {}
  try { setText('WorkersCompensation_RateState_DepositPremiumAmount_A', fmtNum(f.stateDepositPrem)); } catch {}
  try { setText('WorkersCompensation_RateState_RemarkText_A', f.remarks || ''); } catch {}

  // ============ PAGE 3 — PRIOR COVERAGE / LOSS HISTORY (5 rows A-E) ============
  const pcSuffixes = ['_A', '_B', '_C', '_D', '_E'];
  (f.priorCoverage || []).forEach((pc, i) => {
    if (i > 4) return;
    const s = pcSuffixes[i];
    try { setText(`PriorCoverage_InsurerFullName${s}`, pc.carrier); } catch {}
    try { setText(`PriorCoverage_EffectiveYear${s}`, pc.year); } catch {}
    try { setText(`PriorCoverage_TotalPremiumAmount${s}`, fmtNum(pc.premium)); } catch {}
    try { setText(`PriorCoverage_ModificationFactor${s}`, pc.mod); } catch {}
    try { setText(`LossHistory_ClaimCount${s}`, pc.claims); } catch {}
    try { setText(`LossHistory_PaidAmount${s}`, fmtNum(pc.paid)); } catch {}
    try { setText(`LossHistory_ReservedAmount${s}`, fmtNum(pc.reserved)); } catch {}
    try { setText(`PriorCoverage_PolicyNumberIdentifier${s}`, pc.policyNum); } catch {}
  });

  // Operations
  try { setText('CommercialPolicy_OperationsDescription_A', f.operationsDesc || ''); } catch {}

  // ============ PAGE 3 — 19 QUESTIONS ============
  const questionFieldMap = {
    ABE: ['WorkersCompensationLineOfBusiness_Question_ABECode_A', 'WorkersCompensationLineOfBusiness_ApplicantOwnLeaseAircraftOrWatercraftExplanation_A'],
    ACD: ['WorkersCompensationLineOfBusiness_Question_ACDCode_A', 'WorkersCompensationLineOfBusiness_PastPresentDiscontinuedOperationsHazardousMaterialExplanation_A'],
    AAD: ['WorkersCompensationLineOfBusiness_Question_AADCode_A', 'WorkersCompensationLineOfBusiness_WorkPerformedUndergroundOrAboveFifteenFeetExplanation_A'],
    KAR: ['WorkersCompensationLineOfBusiness_Question_KARCode_A', 'WorkersCompensationLineOfBusiness_WorkPerformedOnVesselsDocksBridgesExplanation_A'],
    KAS: ['WorkersCompensationLineOfBusiness_Question_KASCode_A', 'WorkersCompensationLineOfBusiness_ApplicantEngagedAnyOtherTypeOfBusinessExplanation_A'],
    KAT: ['WorkersCompensationLineOfBusiness_Question_KATCode_A', 'WorkersCompensationLineOfBusiness_SubcontractorsUsedExplanation_A'],
    KAU: ['WorkersCompensationLineOfBusiness_Question_KAUCode_A', 'WorkersCompensationLineOfBusiness_AnyWorkSubletWithoutCertificatesOfInsuranceExplanation_A'],
    ABC: ['WorkersCompensationLineOfBusiness_Question_ABCCode_A', 'WorkersCompensationLineOfBusiness_WrittenSafetyProgramInOperationExplanation_A'],
    ABI: ['WorkersCompensationLineOfBusiness_Question_ABICode_A', 'WorkersCompensationLineOfBusiness_AnyGroupTransportationProvidedExplanation_A'],
    AAE: ['WorkersCompensationLineOfBusiness_Question_AAECode_A', 'WorkersCompensationLineOfBusiness_AnyEmployeesUnderSixteenOrOverSixtyYearsExplanation_A'],
    KAV: ['WorkersCompensationLineOfBusiness_Question_KAVCode_A', 'WorkersCompensationLineOfBusiness_AnySeasonalEmployeesExplanation_A'],
    AAF: ['WorkersCompensationLineOfBusiness_Question_AAFCode_A', 'WorkersCompensationLineOfBusiness_VolunteerOrDonatedLabourExplanation_A'],
    ABJ: ['WorkersCompensationLineOfBusiness_Question_ABJCode_A', 'WorkersCompensationLineOfBusiness_EmployeesWithPhysicalHandicapsExplanation_A'],
    ABH: ['WorkersCompensationLineOfBusiness_Question_ABHCode_A', 'WorkersCompensationLineOfBusiness_EmployeesTravelOutOfStateExplanation_A'],
    AAB: ['WorkersCompensationLineOfBusiness_Question_AABCode_A', 'WorkersCompensationLineOfBusiness_AthleticTeamsSponsoredExplanation_A'],
    ACB: ['WorkersCompensationLineOfBusiness_Question_ACBCode_A', 'WorkersCompensationLineOfBusiness_PhysicalsRequiredAfterOffersOfEmploymentAreMadeExplanation_A'],
    ABA: ['WorkersCompensationLineOfBusiness_Question_ABACode_A', 'WorkersCompensationLineOfBusiness_OtherInsuranceWithThisInsurerExplanation_A'],
    AAI: ['WorkersCompensationLineOfBusiness_Question_AAICode_A', 'WorkersCompensationLineOfBusiness_PriorCoverageDeclinedCancelledNonRenewedLastThreeYearsExplanation_A'],
    ABF: ['WorkersCompensationLineOfBusiness_Question_ABFCode_A', 'WorkersCompensationLineOfBusiness_EmployeeHealthPlansProvidedExplanation_A'],
  };

  const questions = f.questions || {};
  const explanations = f.questionExplanations || {};
  Object.keys(questionFieldMap).forEach(code => {
    const [codeField, explField] = questionFieldMap[code];
    try { setText(codeField, questions[code] || ''); } catch {}
    try { setText(explField, explanations[code] || ''); } catch {}
  });

  // ============ PAGE 4 — 5 MORE QUESTIONS ============
  const questionFieldMapP4 = {
    KAW: ['WorkersCompensationLineOfBusiness_Question_KAWCode_A', 'WorkersCompensationLineOfBusiness_AnyEmployeesPerformWorkForOtherBusinessesExplanation_A'],
    AAG: ['WorkersCompensationLineOfBusiness_Question_AAGCode_A', 'WorkersCompensationLineOfBusiness_LeaseEmployeesToOrFromOtherEmployersExplanation_A'],
    ABG: ['WorkersCompensationLineOfBusiness_Question_ABGCode_A', 'WorkersCompensationLineOfBusiness_EmployeesPredominantlyWorkAtHomeExplanation_A'],
    KAX: ['WorkersCompensationLineOfBusiness_Question_KAXCode_A', 'WorkersCompensationLineOfBusiness_TaxLiensOrBankruptcyWithinLastFiveYearsExplanation_W'],
    KAY: ['WorkersCompensationLineOfBusiness_Question_KAYCode_A', 'WorkersCompensationLineOfBusiness_UndisputedUnpaidWorkersCompensationPremiumDueExplanation_A'],
  };

  Object.keys(questionFieldMapP4).forEach(code => {
    const [codeField, explField] = questionFieldMapP4[code];
    try { setText(codeField, questions[code] || ''); } catch {}
    try { setText(explField, explanations[code] || ''); } catch {}
  });

  // ABG at-home count
  try { setText('WorkersCompensationLineOfBusiness_Question_AtHomeEmployeeCount_A', f.questionAtHomeCount || ''); } catch {}

  // ============ LOSS RUN / INFO PRACTICES CHECKBOXES ============
  if (f.lossRunAttached) try { setCheck('WorkersCompensationLineOfBusiness_Attachment_LossRunIndicator_A'); } catch {}
  if (f.infoPracticesNotice) try { setCheck('Policy_InformationPracticesNoticeIndicator_A'); } catch {}

  // ============ PAGE 4 — SIGNATURE ============
  try { setText('NamedInsured_Initials_A', ''); } catch {}
  try { setText('NamedInsured_Signature_A', ins.CompanyName || ''); } catch {}
  try { setText('NamedInsured_Signature_B', ''); } catch {}
  try { setText('NamedInsured_SignatureDate_A', f.date || today); } catch {}
  try { setText('Producer_AuthorizedRepresentative_Signature_A', 'Marc Fragos'); } catch {}
  try { setText('Producer_NationalIdentifier_A', ''); } catch {}

  // ============ Save and open ============
  const filledBytes = await pdfDoc.save({ updateFieldAppearances: false });
  const blob = new Blob([filledBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
}
